import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon, Select, Stepper, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  DEFAULT_PAGE_SIZE,
  operatorPlanListResponseSchema,
  operatorTenantListResponseSchema,
  operatorTenantSchema,
  tenantStatusSchema,
  type OperatorPlan,
  type OperatorTenant,
  type TenantStatus,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey } from '../i18n';
import { useOperatorSession } from '../session';
import { useHealth } from '../use-health';
import { HealthStrip } from './health-strip';
import { ActionDialog, DialogField } from '../chrome/action-dialog';
import { DateFilter } from '../chrome/date-filter';
import { formatDate, formatMoney, formatNumber } from '../chrome/format';
import { HeaderActions } from '../chrome/page-chrome';
import { Pager } from '../chrome/pager';
import { useToast } from '../chrome/toasts';
import { BTN_SM, FIELD, PILL, SELECT_SKIN, TH, TH_ROW, TONE } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Every organisation on the installation. The console's landing screen.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE REDESIGN'S MARKUP, CLASS FOR CLASS.** The first version of this screen used `ui`'s
 * `ListToolbar` and `Table` and looked like a product list: a full-width bordered search box, a
 * filter button, link-blue names, plain-text statuses and a "load more". The mockup draws a
 * borderless search, a status picker with counts, a created-from/to range, coloured status pills,
 * a whole clickable row and the settled pager. Ported here verbatim.
 *
 * ⚠️⚠️ **EVERY FILTER IS THE SERVER'S** — CLAUDE.md §3, *"never fetch-then-filter-in-memory"*. The
 * date range and the per-status counts did not exist on the API and were added for this screen,
 * rather than drawn as controls that do nothing.
 *
 * ⚠️ **The status filter lives in the address**, so the *Felfüggesztett szervezet* tile can link to
 * `?status=suspended` and land on exactly that list.
 */

const STATUS_LABEL: Readonly<Record<TenantStatus, MessageKey>> = {
  trial: 'status.trial',
  active: 'status.active',
  suspended: 'status.suspended',
  awaiting_signup: 'status.awaitingSignup',
};

/** The mockup's `ST` table, tokens per S-17. */
export const STATUS_TONE: Readonly<Record<TenantStatus, string>> = {
  active: TONE.ok,
  trial: TONE.primary,
  suspended: TONE.warn,
  awaiting_signup: TONE.muted,
};

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly code: string }
  | {
      readonly state: 'ready';
      readonly tenants: readonly OperatorTenant[];
      readonly total: number;
      readonly counts: Readonly<Record<string, number>> | undefined;
    };

export function TenantsScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const navigate = useNavigate();
  const health = useHealth();
  const [search, setSearch] = useSearchParams();

  const statusParam = tenantStatusSchema.safeParse(search.get('status'));
  const status: TenantStatus | null = statusParam.success ? statusParam.data : null;

  const [q, setQ] = useState('');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [plans, setPlans] = useState<readonly OperatorPlan[]>([]);
  const [inviting, setInviting] = useState(false);
  const [inviteKey, setInviteKey] = useState(0);

  /* A new filter starts from the first page; staying on page 4 of a list that now has one page
     would be an empty table that is not empty. */
  useEffect(() => {
    setPage(0);
  }, [q, from, to, status]);

  const read = useCallback(async (): Promise<void> => {
    const query = new URLSearchParams();
    if (page > 0) query.set('offset', String(page * DEFAULT_PAGE_SIZE));
    if (status !== null) query.set('status', status);
    /* Trimmed, and omitted when empty: `q` has a `min(1)` bound, so an empty string is a 400. */
    if (q.trim() !== '') query.set('q', q.trim());
    if (from !== null) query.set('createdFrom', from);
    if (to !== null) query.set('createdTo', to);

    const suffix = query.toString() === '' ? '' : `?${query.toString()}`;
    const result = await session.request<unknown>(`${CONTROL_PANEL_PATHS.tenants}${suffix}`);
    if (!result.ok) {
      /* A 401 has already ended the session; this screen is about to unmount. */
      if (result.status === 401) return;
      setLoad({ state: 'failed', code: result.code });
      return;
    }
    const parsed = operatorTenantListResponseSchema.safeParse(result.value);
    /* ⚠️ A 200 that does not match the contract is a FAILURE, not an empty list — read through, it
       would say "no organisations", the most misleading thing this screen could say. */
    setLoad(
      parsed.success
        ? {
            state: 'ready',
            tenants: parsed.data.data,
            total: parsed.data.meta.total,
            counts: parsed.data.counts,
          }
        : { state: 'failed', code: 'unknown' },
    );
  }, [session, page, status, q, from, to]);

  useEffect(() => {
    void read();
  }, [read]);

  /* The catalogue: currency for the monthly value, *visszavonva* for a withdrawn plan, and the
     invitation dialog's plan list. */
  useEffect(() => {
    void session.request<unknown>(CONTROL_PANEL_PATHS.plans).then((result) => {
      if (!result.ok) return;
      const parsed = operatorPlanListResponseSchema.safeParse(result.value);
      if (parsed.success) setPlans(parsed.data.data);
    });
  }, [session]);

  const planById = useMemo(() => new Map(plans.map((plan) => [plan.id, plan])), [plans]);

  const filtered = status !== null || q.trim() !== '' || from !== null || to !== null;
  const clear = (): void => {
    setQ('');
    setFrom(null);
    setTo(null);
    setSearch({});
  };

  const statusOptions = (['all', 'active', 'trial', 'suspended', 'awaiting_signup'] as const).map(
    (value) => {
      const counts = load.state === 'ready' ? load.counts : undefined;
      const hint =
        counts === undefined
          ? undefined
          : value === 'all'
            ? Object.values(counts).reduce((sum, n) => sum + n, 0)
            : (counts[value] ?? 0);
      return {
        value,
        label: value === 'all' ? t('tenants.filter.all') : t(STATUS_LABEL[value]),
        ...(hint === undefined ? {} : { hint: formatNumber(hint, locale) }),
      };
    },
  );

  const total = load.state === 'ready' ? load.total : 0;

  return (
    <div data-screen="tenants" className="flex flex-col gap-5 p-5">
      <HeaderActions>
        <Button
          size="sm"
          className={BTN_SM}
          onClick={() => {
            setInviteKey((key) => key + 1);
            setInviting(true);
          }}
        >
          <Icon name="mail" size={14} />
          {t('tenants.invite')}
        </Button>
      </HeaderActions>

      <HealthStrip state={health} />

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-muted/50 hover:border-input focus-within:border-ring flex h-8 w-[240px] items-center gap-2 rounded-md border border-transparent px-2.5 transition-colors">
            <Icon name="search" size={14} className="text-muted-foreground shrink-0" />
            <input
              aria-label={t('tenants.search')}
              className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-[13px] outline-none"
              value={q}
              onChange={(event) => {
                setQ(event.target.value);
              }}
              placeholder={t('tenants.search')}
            />
          </div>
          <div className="w-[170px]">
            <Select
              aria-label={t('tenants.filter')}
              icon="funnel"
              options={statusOptions}
              value={status ?? 'all'}
              onChange={(event) => {
                const value = event.target.value;
                setSearch(value === 'all' ? {} : { status: value });
              }}
              className={SELECT_SKIN}
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground pl-1 text-[12px] whitespace-nowrap">
              {t('tenants.from')}
            </span>
            <DateFilter value={from} onChange={setFrom} label={t('tenants.from')} />
            <span className="text-muted-foreground text-[12px]">{t('tenants.to')}</span>
            <DateFilter value={to} onChange={setTo} label={t('tenants.to')} />
          </div>
          {filtered ? (
            <Button variant="ghost" size="sm" className={BTN_SM} onClick={clear}>
              {t('tenants.clearFilters')}
            </Button>
          ) : null}
        </div>

        {load.state === 'failed' ? (
          <div role="alert" className="flex flex-col items-start gap-2 rounded-lg border p-4">
            <p className="text-[13px]">{t('tenants.failed')}</p>
            <Button
              variant="outline"
              size="sm"
              className={BTN_SM}
              onClick={() => {
                void read();
              }}
            >
              {t('tenants.retry')}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table aria-label={t('tenants.heading')} className="w-full min-w-[960px] text-[13px]">
              <thead className="bg-muted/40 border-b">
                <tr className={TH_ROW}>
                  <th className={TH}>{t('tenants.column.name')}</th>
                  <th className={TH}>{t('tenants.column.status')}</th>
                  <th className={cn(TH, 'text-right')}>{t('tenants.column.workspaces')}</th>
                  <th className={cn(TH, 'text-right')}>{t('tenants.column.members')}</th>
                  <th className={cn(TH, 'text-right')}>{t('tenants.column.seats')}</th>
                  <th className={TH}>{t('tenants.column.plan')}</th>
                  <th className={TH}>{t('tenants.column.trial')}</th>
                  <th className={cn(TH, 'text-right')}>{t('tenants.column.value')}</th>
                  <th className={TH}>{t('tenants.column.created')}</th>
                </tr>
              </thead>
              <tbody>
                {load.state === 'loading' ? (
                  <SkeletonRows columns={9} rightAligned={[2, 3, 4, 7]} />
                ) : (
                  load.tenants.map((tenant) => (
                    <Row
                      key={tenant.id}
                      tenant={tenant}
                      plan={tenant.planId === null ? undefined : planById.get(tenant.planId)}
                      onOpen={() => {
                        void navigate(`/tenants/${encodeURIComponent(tenant.id)}`);
                      }}
                    />
                  ))
                )}
              </tbody>
            </table>
            {load.state === 'loading' ? (
              <p role="status" className="sr-only">
                {t('tenants.loading')}
              </p>
            ) : null}
            {load.state === 'ready' && load.tenants.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                <span className="text-[13px]">
                  {filtered ? t('tenants.emptyFilter') : t('tenants.empty')}
                </span>
                {filtered ? (
                  <Button variant="outline" size="sm" className={BTN_SM} onClick={clear}>
                    {t('tenants.clearFilters')}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <Pager
          page={page}
          pageCount={Math.ceil(total / DEFAULT_PAGE_SIZE)}
          total={
            load.state === 'loading'
              ? ''
              : t('tenants.count', { count: formatNumber(total, locale) })
          }
          onChange={setPage}
        />
      </section>

      <InviteDialog
        /* A fresh mount per opening: the fields start empty without an effect resetting them. */
        key={inviteKey}
        open={inviting}
        plans={plans}
        onClose={() => {
          setInviting(false);
        }}
        onInvited={() => {
          setInviting(false);
          void read();
        }}
      />
    </div>
  );
}

/**
 * ⚠️ **The whole row opens the organisation, as in the mockup — and the name is ALSO a link**, so a
 * middle-click opens it in a tab and the reachability check can see the entrance. Styled as the
 * mockup's plain text; the base stylesheet would otherwise paint it link-blue.
 */
function Row({
  tenant,
  plan,
  onOpen,
}: {
  readonly tenant: OperatorTenant;
  readonly plan: OperatorPlan | undefined;
  readonly onOpen: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const over = tenant.seatLimit !== null && tenant.seatsUsed > tenant.seatLimit;
  const trialSoon =
    tenant.trialEndsAt !== null &&
    (new Date(tenant.trialEndsAt).getTime() - Date.now()) / 86_400_000 <= 3;

  return (
    <tr className="hover:bg-muted/50 h-10 cursor-pointer border-b last:border-0" onClick={onOpen}>
      <td className="max-w-[280px] px-3">
        <Link
          to={`/tenants/${encodeURIComponent(tenant.id)}`}
          className="text-foreground hover:text-foreground block truncate font-medium"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {tenant.name}
        </Link>
        <span className="text-muted-foreground block truncate font-mono text-[11px]">
          {tenant.status === 'awaiting_signup' && tenant.invitedEmail !== null
            ? tenant.invitedEmail
            : tenant.slug}
        </span>
      </td>
      <td className="px-3">
        <span className={cn(PILL, STATUS_TONE[tenant.status])}>
          {/* `0202` · an invitation that was accepted is waiting on setup, not on a sign-up. */}
          {tenant.status === 'awaiting_signup' && tenant.setupPendingSince !== null
            ? t('status.awaitingSetup')
            : t(STATUS_LABEL[tenant.status])}
        </span>
      </td>
      <td className="px-3 text-right tabular-nums">
        {formatNumber(tenant.workspaceCount, locale)}
      </td>
      <td className="px-3 text-right tabular-nums">{formatNumber(tenant.memberCount, locale)}</td>
      <td
        className={cn(
          'px-3 text-right whitespace-nowrap tabular-nums',
          over ? 'text-destructive font-medium' : '',
        )}
      >
        {`${formatNumber(tenant.seatsUsed, locale)} / ${
          tenant.seatLimit === null ? '∞' : formatNumber(tenant.seatLimit, locale)
        }`}
      </td>
      <td className="px-3 whitespace-nowrap">
        {tenant.planName ?? '–'}
        {plan !== undefined && !plan.isActive ? (
          <span className="text-muted-foreground text-[11.5px]">{` · ${t('tenants.withdrawn')}`}</span>
        ) : null}
      </td>
      <td
        className={cn(
          'px-3 whitespace-nowrap tabular-nums',
          tenant.trialEndsAt === null
            ? 'text-muted-foreground'
            : trialSoon
              ? 'font-medium text-[var(--status-warn-text)]'
              : '',
        )}
      >
        {formatDate(tenant.trialEndsAt, locale)}
      </td>
      <td className="px-3 text-right whitespace-nowrap tabular-nums">
        {tenant.monthlyValue === null || plan === undefined
          ? '–'
          : formatMoney(tenant.monthlyValue, plan.currency, locale)}
      </td>
      <td className="text-muted-foreground px-3 whitespace-nowrap tabular-nums">
        {formatDate(tenant.createdAt, locale)}
      </td>
    </tr>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Szervezet meghívása* — the mockup's dialog, plus the name it no longer asks for.
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE FIELD MORE THAN THE MOCKUP, AND THE RULING IS WHY.** The redesign has the admin name
 * the organisation on sign-up; the ruling of 7 Oct (`decisions.md` S-18) moved the name into the
 * invitation, so the organisation exists while it waits. The field sits first, in the mockup's
 * field style; everything below it — admin, plan, seats — is the drawing's.
 */
function InviteDialog({
  open,
  plans,
  onClose,
  onInvited,
}: {
  readonly open: boolean;
  readonly plans: readonly OperatorPlan[];
  readonly onClose: () => void;
  readonly onInvited: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const toast = useToast();

  const offered = plans.filter((plan) => plan.isActive);
  const [name, setName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [planId, setPlanId] = useState('');
  const [seats, setSeats] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{
    field: 'name' | 'email' | null;
    message: string;
  } | null>(null);

  /* ⚠️ The first plan on offer is the default, as in the mockup — a dialog that opened on "no plan"
     would invite an organisation with no cap by not touching anything. Derived rather than set in
     an effect, so it is right the moment the catalogue arrives. The fields themselves start empty
     on every opening because the screen remounts this dialog with a fresh `key`. */
  const effectivePlanId = planId === '' ? (offered[0]?.id ?? '') : planId;
  const plan = plans.find((candidate) => candidate.id === effectivePlanId);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim());
  const ready =
    name.trim().length > 0 && adminName.trim().length > 1 && emailOk && plan !== undefined;

  const submit = (): void => {
    if (!ready || busy) return;
    setBusy(true);
    setFailure(null);
    void session
      .request<unknown>(CONTROL_PANEL_PATHS.inviteTenant, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          adminName: adminName.trim(),
          adminEmail: email.trim(),
          planId: effectivePlanId,
          ...(seats === '' ? {} : { seatLimit: Number(seats) }),
        }),
      })
      .then((result) => {
        setBusy(false);
        if (!result.ok) {
          if (result.status === 401) return;
          setFailure(refusalOf(result.code, t));
          return;
        }
        if (!operatorTenantSchema.safeParse(result.value).success) return;
        toast({ title: t('tenants.invite.done.title'), description: email.trim() });
        onInvited();
      });
  };

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={t('tenants.invite.title')}
      description={t('tenants.invite.lede')}
      cancelLabel={t('tenants.invite.cancel')}
      confirm={{
        label: busy ? t('tenants.invite.working') : t('tenants.invite.confirm'),
        onClick: submit,
        disabled: !ready || busy,
      }}
      blockedText={failure?.field === null ? failure.message : ''}
    >
      <DialogField
        label={t('tenants.invite.name')}
        htmlFor="invite-org-name"
        {...(failure?.field === 'name' ? { error: failure.message } : {})}
      >
        <input
          id="invite-org-name"
          className={FIELD}
          value={name}
          autoComplete="off"
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </DialogField>
      <DialogField label={t('tenants.invite.adminName')} htmlFor="invite-org-admin">
        <input
          id="invite-org-admin"
          className={FIELD}
          value={adminName}
          autoComplete="off"
          onChange={(event) => {
            setAdminName(event.target.value);
          }}
        />
      </DialogField>
      <DialogField
        label={t('tenants.invite.adminEmail')}
        htmlFor="invite-org-email"
        {...(failure?.field === 'email' ? { error: failure.message } : {})}
      >
        <input
          id="invite-org-email"
          className={FIELD}
          value={email}
          autoComplete="off"
          placeholder={t('tenants.invite.emailPlaceholder')}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </DialogField>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-medium">{t('tenants.invite.plan')}</span>
          <Select
            aria-label={t('tenants.invite.plan')}
            options={offered.map((option) => ({ value: option.id, label: option.name }))}
            value={effectivePlanId}
            onChange={(event) => {
              setPlanId(event.target.value);
            }}
            className={cn(SELECT_SKIN, 'border-input')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-medium">{t('tenants.invite.seats')}</span>
          <Stepper
            value={seats}
            onChange={setSeats}
            min={0}
            decrementLabel={t('stepper.decrement')}
            incrementLabel={t('stepper.increment')}
            className="bg-muted/50"
          />
        </div>
      </div>
      <span className="text-muted-foreground -mt-2 text-[12px]">
        {seats === ''
          ? t('tenants.invite.seatsFromPlan', {
              limit:
                plan?.seatLimit == null
                  ? t('tenants.invite.unlimited')
                  : formatNumber(plan.seatLimit, locale),
            })
          : t('tenants.invite.seatsOverride', { count: seats })}
      </span>
    </ActionDialog>
  );
}

/** One sentence per refusal, attached to the field it is about. Rule 5: by `code`. */
function refusalOf(
  code: string,
  t: ReturnType<typeof useT>,
): { field: 'name' | 'email' | null; message: string } {
  if (code === 'unsluggable_name')
    return { field: 'name', message: t('tenants.invite.error.unsluggable') };
  if (code === 'already_member')
    return { field: 'email', message: t('tenants.invite.error.alreadyMember') };
  if (code === 'is_an_operator')
    return { field: 'email', message: t('tenants.invite.error.isAnOperator') };
  if (code === 'unknown_plan') return { field: null, message: t('error.unknown_plan') };
  if (code === 'unreachable') return { field: null, message: t('error.unreachable') };
  return { field: null, message: t('error.unknown') };
}
